import React, { useEffect, useState } from "react";

export interface TxtViewerProps {
  /**
   * Plain text, or a `blob:` / `http(s):` URL to fetch as UTF-8 text
   * (same pattern as DocxViewer).
   */
  source: string;
  fileName?: string;
}

function isFetchableUrl(s: string): boolean {
  return (
    s.startsWith("blob:") || s.startsWith("http://") || s.startsWith("https://")
  );
}

const TxtViewer: React.FC<TxtViewerProps> = ({ source, fileName }) => {
  const [text, setText] = useState<string>("");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setError(null);

    if (!source) {
      setText("");
      return;
    }

    if (!isFetchableUrl(source)) {
      setText(source);
      return;
    }

    (async () => {
      try {
        const isHttp =
          source.startsWith("http://") || source.startsWith("https://");
        const res = await fetch(source, {
          ...(isHttp
            ? {
                credentials: "include" as RequestCredentials,
                mode: "cors" as RequestMode,
              }
            : {}),
        });
        if (!res.ok) {
          throw new Error(`Failed to load file (${res.status})`);
        }
        const body = await res.text();
        if (!cancelled) setText(body);
      } catch (e) {
        if (!cancelled) {
          setText("");
          setError(
            e instanceof Error ? e.message : "Could not read text file.",
          );
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [source]);

  if (error) {
    return (
      <div className="flex-1 min-h-0 overflow-auto p-6 text-center text-md text-hex-gray-600 border-t border-hex-gray-100">
        {error}
      </div>
    );
  }

  return (
    <div className="flex-1 min-h-0 flex flex-col bg-white border-t border-hex-gray-100">
      <pre
        className="flex-1 min-h-0 m-0 overflow-auto p-4 text-sm leading-relaxed text-hex-gray-900 whitespace-pre-wrap wrap-break-word font-mono"
        style={{ tabSize: 4 }}
        aria-label={
          fileName ? `Text preview: ${fileName}` : "Plain text preview"
        }
      >
        {text}
      </pre>
    </div>
  );
};

export default TxtViewer;
